import { UntypedFormControl, ValidationErrors, ValidatorFn, AbstractControl, UntypedFormGroup, UntypedFormArray } from '@angular/forms';
import * as moment from 'moment';
import { HelperService } from 'src/app/services/utils/helper.service';

/**
 * Validations for Dates
 */
export class DateValidators {
  /**
   * Constructor to inject dependency
   * @param helperService instance of helperservice
   */
  constructor(private helperService: HelperService) {}

  /**
   * validate date less than 1900
   * @param ctrl date control
   */
  static isDateLessThanMinimum(ctrl: UntypedFormControl): ValidationErrors {
    const ctrlValue = ctrl.value;

    if (ctrlValue && DateValidators.isDateField(ctrlValue)) {
      const year = ctrlValue.getFullYear();
      return year < 1900 ? { isDateLessThanMinimum: true } : null;
    } else {
      return null;
    }
  }

  /**
   * validate date more than today
   * @param ctrl date control
   */
  static isDateMoreThanToday(ctrl: UntypedFormControl): ValidationErrors {
    const ctrlValue = ctrl.value;

    if (ctrlValue && DateValidators.isDateField(ctrlValue)) {
      const todayDate = new Date().setHours(0, 0, 0, 0);

      return ctrlValue.setHours(0, 0, 0, 0) > todayDate ? { isDateMoreThanToday: true } : null;
    } else {
      return null;
    }
  }

  /**
   * validate date less than today
   * @param ctrl date control
   */
  static isDateLessThanToday(ctrl: UntypedFormControl): ValidationErrors {
    const ctrlValue = ctrl.value;

    if (ctrlValue && DateValidators.isDateField(ctrlValue)) {
      const todayDate = new Date().setHours(0, 0, 0, 0);

      return ctrlValue.setHours(0, 0, 0, 0) < todayDate ? { isDateLessThanToday: true } : null;
    } else {
      return null;
    }
  }

    /**
   * validate date less than today
   * @param ctrl date control
   */
    static isDateLessThanOrEqualToToday(ctrl: UntypedFormControl): ValidationErrors {
      const ctrlValue = ctrl.value;
  
      if (ctrlValue && DateValidators.isDateField(ctrlValue)) {
        const todayDate = new Date().setHours(0, 0, 0, 0);
  
        return ctrlValue.setHours(0, 0, 0, 0) <= todayDate ? { isDateLessThanOrEqualToToday: true } : null;
      } else {
        return null;
      }
    }

  /**
   * check if date is valid
   * @param ctrl date control
   */
  static isValidDate(ctrl: UntypedFormControl): ValidationErrors {
    const ctrlValue = ctrl.value;

    if (ctrlValue !== '' && ctrlValue !== null) {
      return DateValidators.isDateField(ctrlValue) && ctrlValue.getFullYear() > 1900 ? null : { invalidDate: true };
    } else {
      return null;
    }
  }

  static isTodayDate(ctrl: UntypedFormControl): ValidationErrors {
    const ctrlValue = ctrl.value;
    if (ctrlValue && DateValidators.isDateField(ctrlValue)) {
      const todayDate = new Date().setHours(0, 0, 0, 0);
      return todayDate ? {isTodayDate: true} : null;
  } else {
    return null;
  }
}
  /**
   * compare between two date fields
   * operator will be 'MORE' for comparing endDate has to be more than startDate
   * operator will be 'LESS' for comparing endDate has to be less than startDate
   * @param startDateCtrl name of from date control
   * @param endDateCtrl name of to date control
   * @param operator operator MORE or LESS
   */
  static compareDates(startDateCtrl, endDateCtrl: string, operator: string): ValidatorFn {
    return (ac: AbstractControl) => {
      const startDateVal = ac.get(startDateCtrl).value;
      const endDate = ac.get(endDateCtrl).value;
      let startDate;
      if (startDateVal && startDateVal.value) {
        startDate = startDateVal.value;
      } else {
        startDate = startDateVal;
      }

      if (DateValidators.isDateField(startDate) && DateValidators.isDateField(endDate)) {
        if (operator === 'MORE') {
          return startDate.setHours(0, 0, 0, 0) > endDate.setHours(0, 0, 0, 0) ? { isDateLessThan: true } : null;
        } else if (operator === 'LESS') {
          return startDate.setHours(0, 0, 0, 0) < endDate.setHours(0, 0, 0, 0) ? { isDateMoreThan: true } : null;
        } else if (operator === 'EQUAL') {
          return startDate.setHours(0, 0, 0, 0) != endDate.setHours(0, 0, 0, 0) ? { isDateNotEqual: true } : null;
        }
      }
    };
  }

  /**
   * compare between two date fields
   * operator will be 'MORE' for comparing endDate has to be more than startDate
   * operator will be 'LESS' for comparing endDate has to be less than startDate
   * @param startDateCtrl name of from date control
   * @param endDateCtrl name of to date control
   * @param operator operator MORE or LESS
   */
  static compareValueDateWithStringCtrl(startDateCtrl, endDateCtrl: string, operator: string): ValidatorFn {
    return (ac: AbstractControl) => {
      const startDateVal = startDateCtrl;
      const endDate = ac.get(endDateCtrl).value;
      let startDate;
      if (startDateVal) {
        startDate = new Date(startDateVal);
      }

      if (startDate && DateValidators.isDateField(endDate)) {
        if (operator === 'MORE') {
          return startDate.setHours(0, 0, 0, 0) > endDate.setHours(0, 0, 0, 0) ? { isDateLessThan: true } : null;
        } else if (operator === 'LESS') {
          return startDate.setHours(0, 0, 0, 0) < endDate.setHours(0, 0, 0, 0) ? { isDateMoreThan: true } : null;
        } else if (operator === 'EQUAL') {
          return startDate.setHours(0, 0, 0, 0) != endDate.setHours(0, 0, 0, 0) ? { isDateNotEqual: true } : null;
        }
      }
    };
  }

  /**
   * Check if date is less than the minimum date
   * @param minDate Minimum date
   */
  static dateLessThanMinDate(minDate: string, minDateValidation: string): ValidatorFn {
    return (ctrl: UntypedFormControl) => {
      const minDt = new Date(minDate);
      const date = ctrl.value;

      return DateValidators.isDateField(minDt) && DateValidators.isDateField(date) && minDt.setHours(0, 0, 0, 0) > date.setHours(0, 0, 0, 0)
        ? { isDateLessThanMin: true, date: minDateValidation ? minDateValidation : minDate }
        : null;
    };
  }

  /**
   * validate if the date is more than the max date
   * @param maxDate Maximum date
   */
  static dateLessThanMaxDate(maxDate: string, errorString?): ValidatorFn {
    return (ctrl: UntypedFormControl) => {
      const maxDt = new Date(maxDate);
      const date = ctrl.value;

      return DateValidators.isDateField(maxDt) && DateValidators.isDateField(date) && maxDt.setHours(0, 0, 0, 0) < date.setHours(0, 0, 0, 0)
        ? { isDateMoreThanMax: true, date: errorString ? errorString : maxDate }
        : null;
    };
  }

  /**
   * Check if days difference between from and to date is between the numberOfDays
   * @param startDateCtrl name of the from date control
   * @param endDateCtrl name of the to date control
   * @param numberOfDays days difference between two dates
   */
  static dateLessThanXDays(startDateCtrl: string, endDateCtrl: string, numberOfDays: number): ValidatorFn {
    return (ac: AbstractControl) => {
      const startDate = ac.get(startDateCtrl).value;
      const endDate = ac.get(endDateCtrl).value;
      const daysInMS = 60 * 60 * 24 * 1000 * numberOfDays;

      if (
        DateValidators.isDateField(startDate) &&
        DateValidators.isDateField(endDate) &&
        startDate.toDateString() !== 'Invalid Date' &&
        endDate.toDateString() !== 'Invalid Date'
      ) {
        return endDate.getTime() - startDate.getTime() > daysInMS ? { isDateLessThanXDays: true } : null;
      } else {
        return null;
      }
    };
  }

  /**
   * utility method to check if the field is of type date
   * @param value date value
   */
  public static isDateField(value) {
    return Object.prototype.toString.call(value) === '[object Date]';
  }

  /**
   * compare between two date fields, where onedate is in FormGroup and the other date is within formArray
   * operator will be 'MORE' for comparing endDate has to be more than startDate
   * operator will be 'LESS' for comparing endDate has to be less than stateDate
   * @param startDateCtrl name of from date control
   * @param formArrayName name of the formArray
   * @param endDateCtrl name of to date control
   * @param operator operator MORE or LESS
   * @param thanString name for message
   */
  static compareDateList(
    startDateCtrl: string,
    formArrayName: string,
    endDateCtrl: string,
    operator: string,
    thanString: string
  ): ValidatorFn {
    return (ac: UntypedFormGroup) => {
      let isError = false;
      const startDateVal = ac.get(startDateCtrl).value;
      const arrayList = (ac.get(formArrayName) as UntypedFormArray).controls;
      arrayList.forEach((fg: UntypedFormGroup) => {
        const endDateControl = fg.get(endDateCtrl);
        if (endDateControl && endDateControl.status !== 'DISABLED') {
          const endDate = endDateControl.value;
          let startDate;
          if (startDateVal && startDateVal.value) {
            startDate = startDateVal.value;
          } else {
            startDate = startDateVal;
          }

          if (DateValidators.isDateField(startDate) && DateValidators.isDateField(endDate)) {
            if (operator === 'MORE') {
              if (startDate.setHours(0, 0, 0, 0) > endDate.setHours(0, 0, 0, 0)) {
                isError = true;
                endDateControl.setErrors({ isDateLessThan: true, date: thanString });
              } else {
                endDateControl.setErrors(null);
              }
            } else if (operator === 'LESS') {
              if (startDate.setHours(0, 0, 0, 0) < endDate.setHours(0, 0, 0, 0)) {
                endDateControl.setErrors({ isDateMoreThan: true, date: thanString });
              } else {
                endDateControl.setErrors(null);
              }
            }
          }
        }
      });
      return isError ? { dateError: true } : null;
    };
  }

  /**
   * check if the date entered is present in the list
   * @param list list of items to be traversed
   * @param datePropertyToCheckAgainst date property in the list
   * @param validationString error message
   */
  static isDateAlreadPresentInList(list: any[], datePropertyToCheckAgainst: string, validationString: string) {
    return (ac: UntypedFormControl) => {
      let isError = false;
      const dateValue = ac.value;
      if (DateValidators.isDateField(dateValue) && list) {
        for (const data of list) {
          isError = isError || data[datePropertyToCheckAgainst] === moment(dateValue).format('MM/DD/YYYY');
        }
      }
      return isError ? { isDatePresent: true, date: validationString } : null;
    };
  }

  /**
   * compare between two date fields
   * operator will be 'MORE' for comparing endDate has to be more than startDate
   * operator will be 'LESS' for comparing endDate has to be less than stateDate
   * @param startDateCtrl name of from date control
   * @param endDateCtrl name of to date control
   * @param operator operator MORE or LESS
   */
  static compareDateAndTime(startDateCtrl: string, endDateCtrl: string, operator: string): ValidatorFn {
    return (ac: AbstractControl) => {
      const startDateVal = ac.get(startDateCtrl).value;
      const endDate = ac.get(endDateCtrl).value;
      let startDate;
      if (startDateVal && startDateVal.value) {
        startDate = startDateVal.value;
      } else {
        startDate = startDateVal;
      }

      if (DateValidators.isDateField(startDate) && DateValidators.isDateField(endDate)) {
        if (operator === 'MORE') {
          return startDate > endDate ? { isDateLessThan: true } : null;
        } else if (operator === 'LESS') {
          return startDate < endDate ? { isDateMoreThan: true } : null;
        }
      }
    };
  }

  /**
   * check if age is less than minimum age
   * calculate age from the date entered and checked against the minimum age
   * @param minAge minimum age
   */
  isAgeLessThan(minAge) {
    return (ac: UntypedFormControl) => {
      const dateVal = ac.value;
      let valid = true;
      if (Object.prototype.toString.call(dateVal) === '[object Date]' && dateVal.toDateString() !== 'Invalid Date') {
        const ageFromDateEntered = this.helperService.calculateAge(this.helperService.formatDate(dateVal));
        valid = ageFromDateEntered.years >= minAge;
      }
      return valid ? null : { isAgeLess: true, minAge };
    };
  }
}
